// src/components/FinishLynx/FileUploadAccordions.js

import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  SportsSoccer as TrackIcon,
  FitnessCenter as FieldIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  FilePresent as GenericFileIcon
} from '@mui/icons-material';
import EventManagerFiles from '../../components/gestionEvento/integraciones/EventManagerFiles';
import PhotoFinishUploader from './PhotoFinishUploader';
import TrackFileManager from './TrackFileManager';


// Componente para subir archivos LFF (eventos de campo)
const FieldEventsUploader = ({ onFileUpload }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const lffFiles = files.filter(file => file.name.toLowerCase().endsWith('.lff'));
    setSelectedFiles(prev => [...prev, ...lffFiles]);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    setUploadError(null);
    
    try {
      // Aquí iría la lógica para subir los archivos
      if (onFileUpload) {
        await onFileUpload(selectedFiles, 'field');
      }
      
      // Si todo sale bien, limpiamos la lista
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error uploading field files:', error);
      setUploadError('Hubo un error al subir los archivos. Inténtalo de nuevo.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="body1" gutterBottom>
        Sube archivos LFF para procesar eventos de campo (saltos, lanzamientos)
      </Typography>
      
      <Box sx={{ mb: 2, mt: 2 }}>
        <input
          accept=".lff"
          style={{ display: 'none' }}
          id="field-file-upload"
          type="file"
          multiple
          onChange={handleFileSelect}
        />
        <label htmlFor="field-file-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<UploadIcon />}
            disabled={uploading}
          >
            Seleccionar archivos LFF
          </Button>
        </label>
      </Box>

      {selectedFiles.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {selectedFiles.length} archivo(s) seleccionado(s)
          </Typography>
          <List dense>
            {selectedFiles.map((file, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton 
                    edge="end" 
                    aria-label="delete" 
                    onClick={() => handleRemoveFile(index)}
                    disabled={uploading}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <FieldIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={file.name} 
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                />
              </ListItem>
            ))}
          </List>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            >
              {uploading ? 'Subiendo...' : 'Subir archivos'}
            </Button>
          </Box>
        </Paper>
      )}

      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {uploadError}
        </Alert>
      )}
    </Box>
  );
};

// Componente principal de acordeones
const FileUploadAccordions = ({ onFileUpload }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estado para controlar qué acordeón está expandido
  const [expanded, setExpanded] = useState('general');

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Subida de Archivos
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      {/* Acordeón para EventManagerFiles (gestión general) */}
      <Accordion 
        expanded={expanded === 'general'} 
        onChange={handleAccordionChange('general')}
        sx={{ 
          mb: 1.5,
          '&.Mui-expanded': {
            boxShadow: theme.shadows[3]
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="general-content"
          id="general-header"
          sx={{
            backgroundColor: expanded === 'general' ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
            borderLeft: expanded === 'general' ? `4px solid ${theme.palette.primary.main}` : 'none',
            paddingLeft: expanded === 'general' ? 1.5 : 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <GenericFileIcon color="primary" sx={{ mr: 1.5 }} />
            <Typography variant={isMobile ? "body1" : "subtitle1"}>
              Gestión General de Archivos
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: isMobile ? 1.5 : 2 }}>
          <EventManagerFiles />
        </AccordionDetails>
      </Accordion>
      
      {/* Acordeón para archivos LIF (eventos de pista) */}
      <Accordion 
        expanded={expanded === 'track'} 
        onChange={handleAccordionChange('track')}
        sx={{ 
          mb: 1.5,
          '&.Mui-expanded': {
            boxShadow: theme.shadows[3]
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="track-content"
          id="track-header"
          sx={{
            backgroundColor: expanded === 'track' ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
            borderLeft: expanded === 'track' ? `4px solid ${theme.palette.primary.main}` : 'none',
            paddingLeft: expanded === 'track' ? 1.5 : 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TrackIcon color="primary" sx={{ mr: 1.5 }} />
            <Typography variant={isMobile ? "body1" : "subtitle1"}>
              Archivos de Eventos de Pista (LIF)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: isMobile ? 1.5 : 2 }}>
          <TrackFileManager/>
        </AccordionDetails>
      </Accordion>
      
      {/* Acordeón para archivos LFF (eventos de campo) */}
      <Accordion 
        expanded={expanded === 'field'} 
        onChange={handleAccordionChange('field')}
        sx={{ 
          mb: 1.5,
          '&.Mui-expanded': {
            boxShadow: theme.shadows[3]
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="field-content"
          id="field-header"
          sx={{
            backgroundColor: expanded === 'field' ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
            borderLeft: expanded === 'field' ? `4px solid ${theme.palette.primary.main}` : 'none',
            paddingLeft: expanded === 'field' ? 1.5 : 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FieldIcon color="primary" sx={{ mr: 1.5 }} />
            <Typography variant={isMobile ? "body1" : "subtitle1"}>
              Archivos de Eventos de Campo (LFF)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: isMobile ? 1.5 : 2 }}>
          <FieldEventsUploader onFileUpload={onFileUpload} />
        </AccordionDetails>
      </Accordion>
      
      {/* Acordeón para imágenes de fotofinish */}
      <Accordion 
        expanded={expanded === 'photofinish'} 
        onChange={handleAccordionChange('photofinish')}
        sx={{ 
          mb: 1.5,
          '&.Mui-expanded': {
            boxShadow: theme.shadows[3]
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="photofinish-content"
          id="photofinish-header"
          sx={{
            backgroundColor: expanded === 'photofinish' ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
            borderLeft: expanded === 'photofinish' ? `4px solid ${theme.palette.primary.main}` : 'none',
            paddingLeft: expanded === 'photofinish' ? 1.5 : 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ImageIcon color="primary" sx={{ mr: 1.5 }} />
            <Typography variant={isMobile ? "body1" : "subtitle1"}>
              Imágenes de Fotofinish
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: isMobile ? 1.5 : 2 }}>
          <PhotoFinishUploader onFileUpload={onFileUpload} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default FileUploadAccordions;